import { apiClient, handleApiError } from './api.js';

class HolidayService {
  // Get all holidays
  async getAllHolidays() {
    try {
      return await apiClient.get('/holiday');
    } catch (error) {
      console.error('Error getting all holidays:', error);
      throw handleApiError(error);
    }
  }

  // Get holiday by ID
  async getHolidayById(holidayId) {
    try {
      return await apiClient.get(`/holiday/${holidayId}`);
    } catch (error) {
      console.error(`Error getting holiday ${holidayId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get holidays by date range
  async getHolidaysByDateRange(startDate, endDate) {
    try {
      return await apiClient.get(`/holiday/date-range?startDate=${startDate}&endDate=${endDate}`);
    } catch (error) {
      console.error('Error getting holidays by date range:', error);
      throw handleApiError(error);
    }
  }

  // Check if a specific date is a holiday
  async isHoliday(date) {
    try {
      // Format date if it's a Date object
      const formattedDate = typeof date === 'string' 
        ? date 
        : new Date(date).toISOString().split('T')[0];
      
      const response = await apiClient.get(`/holiday/is-holiday?date=${formattedDate}`);
      return response.data;
    } catch (error) {
      console.error(`Error checking if date ${date} is a holiday:`, error);
      throw handleApiError(error);
    }
  }

  // Create a new holiday
  async createHoliday(holiday) {
    try {
      // Format date if needed - preserve the selected date without timezone conversion
      let holidayDate;
      if (typeof holiday.date === 'string') {
        holidayDate = holiday.date;
      } else if (holiday.date instanceof Date) {
        // Ensure the date is valid before formatting
        if (isNaN(holiday.date.getTime())) {
          throw new Error('Invalid date provided');
        }
        // Create a date string in YYYY-MM-DD format without timezone conversion
        const year = holiday.date.getFullYear();
        const month = String(holiday.date.getMonth() + 1).padStart(2, '0');
        const day = String(holiday.date.getDate()).padStart(2, '0');
        holidayDate = `${year}-${month}-${day}`;
      } else {
        throw new Error('Invalid date format');
      }

      let holidayEndDate = null;
      if (holiday.endDate) {
        if (typeof holiday.endDate === 'string') {
          holidayEndDate = holiday.endDate;
        } else if (holiday.endDate instanceof Date) {
          if (isNaN(holiday.endDate.getTime())) {
            throw new Error('Invalid end date provided');
          }
          const year = holiday.endDate.getFullYear();
          const month = String(holiday.endDate.getMonth() + 1).padStart(2, '0');
          const day = String(holiday.endDate.getDate()).padStart(2, '0');
          holidayEndDate = `${year}-${month}-${day}`;
        } else {
          throw new Error('Invalid end date format');
        }
      }

      if (holidayEndDate && holidayEndDate === holidayDate) {
        holidayEndDate = null;
      }

      // Prepare holiday data with proper casing to match backend model
      const holidayData = {
        HolidayId: 0, // Will be assigned by the backend
        Name: holiday.name,
        Date: holidayDate,
        EndDate: holidayEndDate,
        Description: holiday.description || '',
        IsRecurringYearly: holiday.isRecurringYearly || false // Default to false if not provided
      };
      
      // Get the current token to ensure it's being sent
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }
      
      // Log the request for debugging
      console.log('Creating holiday with data:', holidayData);
      
      // Use the apiClient directly - it already has the token in interceptors
      // and the baseURL already includes /api
      const response = await apiClient.post('/holiday', holidayData);
      
      return response;
    } catch (error) {
      console.error('Error creating holiday:', error);
      
      // Provide more detailed error information
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        
        // Handle specific error cases
        if (error.response.status === 403) {
          throw new Error('You do not have permission to create holidays. Admin role is required.');
        }
      }
      
      throw handleApiError(error);
    }
  }

  // Update an existing holiday
  async updateHoliday(holidayId, holiday) {
    try {
      // Format date if needed - ensure we handle Date objects correctly
      let holidayDate;
      if (typeof holiday.date === 'string') {
        holidayDate = holiday.date;
      } else if (holiday.date instanceof Date) {
        // Ensure the date is valid before formatting
        if (isNaN(holiday.date.getTime())) {
          throw new Error('Invalid date provided');
        }
        // Create a date string in YYYY-MM-DD format without timezone conversion
        const year = holiday.date.getFullYear();
        const month = String(holiday.date.getMonth() + 1).padStart(2, '0');
        const day = String(holiday.date.getDate()).padStart(2, '0');
        holidayDate = `${year}-${month}-${day}`;
      } else {
        throw new Error('Invalid date format');
      }

      let holidayEndDate = null;
      if (holiday.endDate) {
        if (typeof holiday.endDate === 'string') {
          holidayEndDate = holiday.endDate;
        } else if (holiday.endDate instanceof Date) {
          if (isNaN(holiday.endDate.getTime())) {
            throw new Error('Invalid end date provided');
          }
          const year = holiday.endDate.getFullYear();
          const month = String(holiday.endDate.getMonth() + 1).padStart(2, '0');
          const day = String(holiday.endDate.getDate()).padStart(2, '0');
          holidayEndDate = `${year}-${month}-${day}`;
        } else {
          throw new Error('Invalid end date format');
        }
      }

      if (holidayEndDate && holidayEndDate === holidayDate) {
        holidayEndDate = null;
      }

      console.log('Updating holiday with formatted date:', holidayDate);

      // Prepare holiday data with proper casing to match backend model
      const holidayData = {
        HolidayId: holidayId,
        Name: holiday.name,
        Date: holidayDate,
        EndDate: holidayEndDate,
        Description: holiday.description || '',
        IsRecurringYearly: holiday.isRecurringYearly || false // Default to false if not provided
      };
      
      console.log('Sending holiday update data:', holidayData);
      const response = await apiClient.put(`/holiday/${holidayId}`, holidayData);
      return response;
    } catch (error) {
      console.error(`Error updating holiday ${holidayId}:`, error);
      throw handleApiError(error);
    }
  }

  // Delete a holiday
  async deleteHoliday(holidayId) {
    try {
      const response = await apiClient.delete(`/holiday/${holidayId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting holiday ${holidayId}:`, error);
      throw handleApiError(error);
    }
  }

  // Get holidays in a specific date range (returns array of DateTime objects)
  async getHolidaysInRange(startDate, endDate) {
    try {
      // Format dates if needed
      const formattedStartDate = typeof startDate === 'string' 
        ? startDate 
        : new Date(startDate).toISOString().split('T')[0];
      
      const formattedEndDate = typeof endDate === 'string' 
        ? endDate 
        : new Date(endDate).toISOString().split('T')[0];
      
      const response = await apiClient.get(`/holiday/in-range?startDate=${formattedStartDate}&endDate=${formattedEndDate}`);
      return response.data;
    } catch (error) {
      console.error('Error getting holidays in range:', error);
      throw handleApiError(error);
    }
  }
}

export default new HolidayService();
